import React, { FC, ReactNode } from 'react'
import { Box } from './Box'
import type { BoxDto } from '../types/generated'
import { BoxesLayout } from '../layout/BoxesLayout'
import { ScrollLoader } from '../system/ScrollLoader'
import { styled } from '@linaria/react'
import { EmptyList } from './EmptyList'
import { InfiniteData } from '@tanstack/react-query'

export const ScrollBoxes: FC<{
  data: InfiniteData<{ data: BoxDto[] }> | undefined
  fetchNextPage: () => void
  isFetching: boolean
  isFetchingNextPage: boolean
  isError: boolean
  refetch: () => void
  titleElement: ReactNode | null
  tabsElement: ReactNode
  displayFilterFn: (box: BoxDto) => boolean
}> = ({
  data,
  fetchNextPage,
  isFetching,
  isFetchingNextPage,
  isError,
  refetch,
  titleElement,
  tabsElement,
  displayFilterFn,
}) => {
  // Safe filter that catches errors in displayFilterFn
  const safeFilter = (box: BoxDto): boolean => {
    try {
      return displayFilterFn(box)
    } catch {
      return false
    }
  }

  const isEmpty = data?.pages.every(
    page => page.data.filter(safeFilter).length === 0,
  )

  return (
    <BoxesLayout
      loading={isFetching && !isFetchingNextPage && !data}
      error={isError}
      onRetryFetch={refetch}
      titleElement={titleElement}
      tabsElement={tabsElement}
    >
      <ScrollLoader loading={isFetching} onShouldLoad={fetchNextPage}>
        <Container>
          {isEmpty ? (
            <EmptyList key="empty-state" />
          ) : (
            (data?.pages.map(page => {
              return page.data.filter(safeFilter).map(box => {
                // Type guard: Check if box has all required BoxType properties
                if ('question' in box && 'boxChoices' in box) {
                  return <Box key={box.address} box={box} />
                }
                return null
              })
            }) ?? [])
          )}
        </Container>
      </ScrollLoader>
    </BoxesLayout>
  )
}

const Container = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 360px), 1fr));
  gap: 12px;
  min-height: 330px;
`
